import React, { useState, useEffect } from "react";

import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import enableExporting from "highcharts/modules/exporting";

import { Footer } from "../../footer";
import { NavBar } from "../../navbar";
import { Options } from "./options";
import { useCompanies } from "../../../api/queries/companies";
import { useVariables } from "../../../api/queries/variables";
import { useSensors } from "../../../api/queries/sensors";
import { useSites } from "../../../api/queries/sites";
import { useSearchParams } from "react-router-dom";
import { useChartData } from "../../../api/queries/sensorData";
import { Table } from "./table";
import { Loading } from "../../loadingContext";
import { Bars3Icon } from "@heroicons/react/24/solid";

enableExporting(Highcharts);

const STORAGE_KEY = 'charts-search-params';

export const Charts = () => {
  let today = new Date();
  let oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  
  // Helper function to get initial search params
  const getInitialSearchParams = () => {
    // First check if there are URL search params
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.toString()) {
      return { params: urlParams, fromStorage: false };
    }
    
    // If no URL params, try to load from sessionStorage
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { params: new URLSearchParams(stored), fromStorage: true };
      }
    } catch (error) {
      console.warn('Failed to load search params from sessionStorage:', error);
    }
    
    // Fall back to default params
    return { 
      params: new URLSearchParams({
        min: oneWeekAgo.getTime(),
        max: today.getTime(),
      }), 
      fromStorage: false 
    };
  };

  const initialData = getInitialSearchParams();
  let [searchParams, setSearchParams] = useSearchParams(initialData.params);
  
  // Update URL if we loaded from storage
  useEffect(() => {
    if (initialData.fromStorage) {
      setSearchParams(initialData.params, { replace: true });
    }
  }, []); // Only run once on mount
  
  // Save search params to sessionStorage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, searchParams.toString());
    } catch (error) {
      console.warn('Failed to save search params to sessionStorage:', error);
    }
  }, [searchParams]);

  // Clear function to reset everything
  const handleClear = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear sessionStorage:', error);
    }
    
    // Reset to default params (1 week ago to today)
    const defaultParams = new URLSearchParams({
      min: oneWeekAgo.getTime(),
      max: today.getTime(),
    });
    
    // Reset chart state to default (chart view)
    setShowChart(true);
    
    setSearchParams(defaultParams, { replace: true });
  };
  
  let showChartState = searchParams.get("type") === "table" ? false : true;
  const [showChart, setShowChart] = useState(showChartState);
  const companies = useCompanies({ activeOnly: true });
  const sensors = useSensors({ activeOnly: false });
  const sites = useSites();
  const variables = useVariables();
  // Get sensors for selected sites
  let selectedSites = searchParams.getAll("sites") || [];
  let filteredSensors = [];
  
  if (selectedSites.length > 0 && sensors.data) {
    filteredSensors = sensors.data.filter(sensor => 
      selectedSites.includes(sensor.data().SiteID)
    );
  }

  const sensorData = useChartData({
    sensors: filteredSensors.map(sensor => sensor.id) || [],
    startDate: searchParams.get("min"),
    endDate: searchParams.get("max"),
  });
  const selectedOptions = searchParams.size;

  let devices = [];
  if (sensorData.data && filteredSensors.length > 0) {
    console.log("Sensor data loaded for sites:", selectedSites);
    devices = filteredSensors.map(sensor => sensor.data());
  }
  let var1 =
    variables.data?.filter((v) => v.id === searchParams.get("var1"))[0] || null;
  let var2 =
    variables.data?.filter((v) => v.id === searchParams.get("var2"))[0] || null;
  let yAxis = 1;
  let chartSeries = devices?.flatMap((device) => {
    let returnVal = [];
    if (var1) {
      let data = sensorData.data[device.DeviceID]
        .filter((doc) => {
          if (doc.QAFlag) return false;
          
          // Check if data point is within the sensor's active date range
          const dataTime = doc.Time.seconds * 1000; // Convert to milliseconds
          const startTime = device.StartDate ? device.StartDate.toMillis() : 0;
          const endTime = device.EndDate ? device.EndDate.toMillis() : Date.now();
          
          return dataTime >= startTime && dataTime < endTime;
        })
        .map((doc) => {
          return [doc.Time.seconds * 1000, doc[var1.data().ColumnName]];
        });
      returnVal.push({
        data,
        name: device.Name + " - " + var1.data().ColumnName,
      });
    }
    if (var2) {
      let data = sensorData.data[device.DeviceID]
        .filter((doc) => {
          if (doc.QAFlag) return false;
          
          // Check if data point is within the sensor's active date range
          const dataTime = doc.Time.seconds * 1000; // Convert to milliseconds
          const startTime = device.StartDate ? device.StartDate.toMillis() : 0;
          const endTime = device.EndDate ? device.EndDate.toMillis() : Date.now();
          
          return dataTime >= startTime && dataTime <= endTime;
        })
        .map((doc) => {
          return [doc.Time.seconds * 1000, doc[var2.data().ColumnName]];
        });
      if (var1 && var1.id !== var2.id) {
        yAxis = 1;
      }
      returnVal.push({
        data,
        name: device.Name + " - " + var2?.data().ColumnName,
        yAxis: yAxis,
      });
    }
    return returnVal;
  });
  console.log("Chart series data:", chartSeries);

  let title = `${devices.map((device) => device.Name).join(", ")}`;

  let chartOptions = {
    title: {
      text: title,
    },

    series: chartSeries,
    accessibility: {
      enabled: false,
    },
    navigator: {
      enabled: true,
      series: [],
    },
    exporting: {
      buttons: {
        contextButton: {
          symbol: "menuball",
        },
      },
      chartOptions: {
        plotOptions: {
          series: {
            dataLabels: {
              enabled: false,
            },
          },
        },
      },
      fallbackToExportServer: false,
    },
    plotOptions: {
      series: {
        dataGrouping: {
          enabled: true,
          forced: searchParams.get("groupBy") !== undefined,
          units: searchParams.get("groupBy") ? [[searchParams.get("groupBy"), [1]]] : undefined,
        }
      }
    },
    rangeSelector: {
      buttons: [
        {
          type: "hour",
          count: 1,
          text: "1h",
        },
        {
          type: "hour",
          count: 4,
          text: "4h",
        },
        {
          type: "day",
          count: 1,
          text: "1D",
        },
        {
          type: "week",
          count: 1,
          text: "1W",
        },
        {
          type: "month",
          count: 1,
          text: "1M",
        },
        {
          type: "all",
          count: 1,
          text: "All",
        },
      ],
      inputEnabled: false,
      enabled: true,
    },
    xAxis: {
      type: "datetime",
      labels: {
        format: "{value:%e-%b-%Y}",
      },
      min: Number(searchParams.get("min")) || null,
      max: Number(searchParams.get("max")) || null,
    },
    yAxis: [
      {
        title: { text: var1?.data().ColumnName },
        labels: { format: "{value:.2f}" },
      },
      {
        title: { text: var2?.data().ColumnName },
        labels: { format: "{value:.2f}" },
        opposite: true,
      },
    ],
    pilotOptions: {
      series: {
        point: {
          events: {
            mouseOver(e) {},
          },
        },
      },
    },
  };

  return (
    <div>
      <NavBar />
      <div className="min-h-screen">
        <div className="drawer lg:drawer-open">
          <input
            id="chart-options-drawer"
            type="checkbox"
            className="drawer-toggle"
          />
          <div className="drawer-content">
            <div className="flex">
              <label
                htmlFor="chart-options-drawer"
                className="btn btn-ghost btn-sm drawer-button lg:hidden m-2 justify-self-start"
              >
                <Bars3Icon className="h-8 w-8" />
              </label>
            </div>
            {sensorData.isLoading ? (
              <Loading />
            ) : (
              <>
                {selectedOptions > 2 && selectedSites.length > 0 ? (
                  <div className="p-10 pb-0">
                    {showChart ? (
                      <HighchartsReact
                        containerProps={{
                          className: "h-[50lvh] lg:h-auto lg:aspect-video",
                        }}
                        highcharts={Highcharts}
                        options={chartOptions}
                      />
                    ) : (
                      <Table sensorData={sensorData} />
                    )}
                  </div>
                ) : (
                  <h1 className="pt-3">
                    Select sites on the left to see data
                  </h1>
                )}
              </>
            )}
          </div>
          {companies.isLoading || sensors.isLoading || sites.isLoading || variables.isLoading ? (
            <Loading />
          ) : (
            <Options
              companies={companies.data}
              sensors={sensors.data}
              sites={sites.data}
              variables={variables.data}
              setSearchParams={setSearchParams}
              searchParams={searchParams}
              setShowChart={setShowChart}
              showChart={showChart}
              onClear={handleClear}
            />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};
